import { Keys, LogoutReason } from '@eih-netbroker/common';
import { useAuth as useOIDCAuth } from 'oidc-react';
import { useState } from 'react';
import { ReactComponent as ErsteShortLogo } from '../../../assets/icons/erste-short-logo.svg';
import { ReactComponent as ErsteLogo } from '../../../assets/icons/login-page-erste-logo.svg';
import { useDialog } from '../../../context/DialogContext';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useTheme } from '../../../context/NbThemeContext';
import { useSnackbar } from '../../../context/SnackbarContext';
import { NbButton } from '../../../shared/components/button/Button';
import { Config } from '../../../shared/config/config';
import { useAuthorizationFlow } from '../../../shared/services/authorization/useAuthorizationFlow';
import { useOptions } from '../../../shared/services/useOptions';
import classes from './LoginView.module.scss';
import { LoginViewDetails } from './LoginViewDetails';
import { UnsuccessfulLoginModal } from './UnsuccessfulLoginModal';

const instrumentSearchDark = require('../../../assets/images/login-page-instrument-search-dark.png');
const instrumentSearchLight = require('../../../assets/images/login-page-instrument-search-light.png');

export const LoginView = () => {
  const { translation } = useLocale();
  const [isLoading, setIsLoading] = useState(false);
  const { userManager } = useOIDCAuth();
  const { showError, closeAllSnackbars } = useSnackbar();
  const { languageOptions } = useOptions();
  const { loginInternal } = useAuthorizationFlow();
  const { isDark } = useTheme();
  const { openDialog } = useDialog();

  const footerLinks = [
    {
      href: Config.Links.Contact,
      displayName: translation.login.contact,
    },
    {
      href: Config.Links.Legal,
      displayName: translation.login.legalNotice,
    },
    {
      href: Config.Links.Technical,
      displayName: translation.login.technicalInfo,
    },
  ];

  const loginClicked = async () => {
    try {
      setIsLoading(true);
      closeAllSnackbars();
      try {
        await userManager.clearStaleState();
      } catch (err) {}

      const signinResult = await userManager.signinPopup({ ui_locales: languageOptions[0].id });
      await loginInternal(signinResult.access_token, signinResult.id_token || '');
    } catch (err) {
      const errMsg = (err as Error).message;
      if (errMsg === Keys.LoginError.UserCancelled) {
        // do nothing
      } else if (errMsg === Keys.LoginError.UserDisabled) {
        await openDialog((close: () => void) => (
          <UnsuccessfulLoginModal
            onClose={close}
            reason={LogoutReason.UserDisabled}
          />
        ));
      } else if (errMsg === Keys.LoginError.AuthRequired) {
        showError(translation.login.error.authError);
      } else if (errMsg === Keys.LoginError.WrongSMSCode3Times) {
        showError(translation.login.wrongSMSCode3Times.message, {
          title: translation.login.wrongSMSCode3Times.title,
        });
      } else {
        showError(translation.login.error.general);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.background}>
      <div className={classes.wrapper}>
        <div className={classes.wrapperLeftSide}>
          <ErsteLogo className={classes.wrapperLeftSideErsteLogo} />
          <div className={classes.wrapperLeftSideDetailsWrapper}>
            <LoginViewDetails />
          </div>
          <div className={classes.wrapperLeftSideAccount}>
            <div className={classes.wrapperLeftSideAccountRegistration}>
              {translation.login.noAccountYet}
              <a
                href={Config.Links.Registration}
                target={'_blank'}
                rel="noreferrer noopener"
              >
                {translation.login.registration}
              </a>
            </div>
            <NbButton
              className={classes.wrapperLeftSideAccountLoginButton}
              onClick={loginClicked}
              isLoading={isLoading}
              data-testid="login-button"
            >
              <span className={classes.wrapperLeftSideLoginButtonText}>{translation.login.login}</span>
            </NbButton>
          </div>
          <div className={classes.wrapperLeftSideLinks}>
            {footerLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                target={'_blank'}
                rel="noreferrer noopener"
              >
                {link.displayName}
              </a>
            ))}
          </div>
        </div>
        <div className={classes.wrapperRightSide}>
          <img
            className={classes.wrapperRightSideInstrumentSearchImg}
            src={isDark ? instrumentSearchDark : instrumentSearchLight}
            alt=""
          />
          <LoginViewDetails />
        </div>
      </div>
      <ErsteShortLogo className={classes.backgroundIcon} />
    </div>
  );
};
